import React, {useEffect, useState, useCallback, useRef, useMemo} from 'react';
import {useNavigate} from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import { FormControl, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import {
    DataGrid,
    GridFilterModel, GridSortModel, GridLinkOperator, GridRowId, GridFilterItem, GridColDef, GridRenderCellParams
} from "@mui/x-data-grid";
import {useAuth} from "../../_auth/useAuth";
import {
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings, ListViewToolbar,
    DialogBox, IDialogTextProps,
    MessageBox, MsgBoxType, IMessagePanelProps,
    MuiGridStateProp, MuiGridDataProp, muiGridFilterIsValid, FormTitle
} from "../_shared";
import {IItemIdsRequest, IMuiDataRequest, userApi, lookupApi, ILookupUserSearchTerm} from "../../_api";
import {AuthRolesEnum, userViewColumns} from "../../_appData";
import { UserGroup, UserGroupDisplay } from "../../_appData/enums/userGroup";
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';

export const UserListView: React.FC = () => {
    const { isAuthenticated, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    
    // Create columns with actions column that uses navigate with state
    const columns = useMemo(() => {
        const actionsColumn: GridColDef = {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/user/${params.row.id}`, {
                                state: { mode: 'view' }
                            });
                        }}
                        sx={{ 
                            padding: '4px',
                            color: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                );
            },
        };
        return [actionsColumn, ...userViewColumns];
    }, [navigate]);
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    const getGridDataRef = useRef<() => Promise<void>>();
    const [dlgBoxOpen, setDlgBoxOpen] = React.useState<boolean>(false);
    const [dlgBoxProps, setDlgBoxProps] = useState<IDialogTextProps>({message: ""});
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({message: ""});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [gridState, setGridState] = useState<MuiGridStateProp>({
        page: 0,
        page_size: DaGridSettings.defaultPageSize,
        filters: {
            items: [],
            linkOperator: GridLinkOperator.Or
        },
        sorts: [{ field: 'modify_datetime', sort: 'desc' }],
    });
    const [gridData, setGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [groupOptions, setGroupOptions] = useState<Array<{group_id: number | null, group_name: string}>>([]);
    const [isGroupLoading, setIsGroupLoading] = useState<boolean>(false);

    const handleGroupChange = (event: SelectChangeEvent<string>) => {
        setSelectedGroup(event.target.value);
    };

    // Get group options from API (similar to GroupAddDialog)
    const getGroupOptions = async (): Promise<void> => {
        setIsGroupLoading(true);
        const options: ILookupUserSearchTerm = {user_id: -1, search_term: ''};
        const auth_token = await getAccessToken();
        try {
            const resp = await lookupApi.get_user_group_lookup(options, auth_token);
            if (resp) {
                if (resp.success && Array.isArray(resp.data)) {
                    // Set groups exactly as returned by API - group_id may be null, that's fine
                    setGroupOptions(resp.data);
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while retrieving Authorization Group lookups data. " +
                            "The Authorization Group lookups data was not found.",
                        "Authorization Group lookups Not Found Error",
                        "error"
                    );
                    setGroupOptions([]);
                }
            } else {
                setGroupOptions([]);
            }
        }
        catch (err) {
            msgBox_Show(
                "An error occurred while fetching Authorization Group lookups data. " +
                "There may have been a network error.",
                "Lookup API Fetch Error",
                "error"
            );
            setGroupOptions([]);
        }
        finally {
            setIsGroupLoading(false);
        }
    };

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const deleteSelectedItems = useCallback(async (row_ids: Array<string | number>) => {
        setIsLoading(true);

        const options: IItemIdsRequest = {item_ids: row_ids};
        const auth_token: string = await getAccessToken();

        userApi.remove(options, auth_token)
        .then((resp) => {
            if(resp) {
                if (resp.success) {
                    // Call getGridData via ref to refresh the grid
                    if (getGridDataRef.current) {
                        getGridDataRef.current();
                    }
                    msgBox_Show(
                        `${resp.count} User items have been deleted...`,
                        undefined,
                        "info"
                    );
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while attempting to delete User data... " +
                        "please retry you last action. If you continue to experience issues, " +
                        "contact customer support.",
                        "Unable to Delete User data...",
                        "warning"
                    );
                }
            }
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [msgBox_Show, getAccessToken]);

    const dlgBox_Close = useCallback((value?: string) => {
        const selRows = selectionModel;
        setDlgBoxOpen(false);

        if(value && value ==="ok") {
            // delete
            deleteSelectedItems(selRows as (string | number)[]);
        }
    }, [selectionModel, deleteSelectedItems]);

    const dlgBox_Show = (message: string, title?: string) => {
        setDlgBoxProps({
            message: message,
            title: title,
        })
        setDlgBoxOpen(true);
    }

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const onGridPageChange = (page: number) => {
        setGridState((prev) => ({
            ...prev,
            page: page
        }));
    };

    const onGridPageSizeChange = (size: number) => {
        setGridState((prev) => ({
            ...prev,
            page_size: size
        }));
    };

    const onGridFilterChange = (model: GridFilterModel) => {
        if(muiGridFilterIsValid(model)) {
            setGridState((prev) => ({
                ...prev,
                filters: model
            }));
        }
    };

    const onGridSortChange = (model: GridSortModel) => {
        setGridState((prev) => ({
            ...prev,
            sorts: model
        }));
    };

    const onClickNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        navigate("/user/new");
    };

    const onClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selRows = selectionModel;
        if(selRows.length === 0)
            msgBox_Show(
                "No item selected... select one item only to be edited",
                "Edit User...",
                "warning"
            );
        if(selRows.length > 1)
            msgBox_Show(
                "Multiple items selected... select one item only to be edited",
                "Edit User...",
                "warning"
            );

        if(selRows.length === 1){
            navigate(`/user/${selRows[0]}`, {
                state: { mode: 'edit' }
            });
        }
    };

    const onClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgBox_close for processing of dialog result
        dlgBox_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const execSearch = (term: string) => {
        // Modify Grid Filter model
        let model: GridFilterModel = {
            items: [],
            linkOperator: GridLinkOperator.Or
        }
        // Search First Name, Last Name
        if(term.length > 0) {
            model.items.push({id: 1, columnField: "first_name", operatorValue: "contains", value: term});
            model.items.push({id: 2, columnField: "last_name", operatorValue: "contains", value: term});
        }

        setGridState((prev) => ({
            ...prev,
            filters: model
        }));
    };

    const onClickReset = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSelectedGroup('');
        setGridState((prev) => ({
            ...prev,
            filters: {
                items: [],
                linkOperator: GridLinkOperator.Or
            },
            sorts: []
        }));
    };

    const getGridData = useCallback(async () => {
        setIsLoading(true);

        // Remove any groups__name from filters if it somehow got added
        const cleanedFilters = {
            items: (gridState.filters?.items || []).filter(
                item => item.columnField !== 'groups__name'
            ),
            linkOperator: gridState.filters?.linkOperator || GridLinkOperator.Or
        };

        // Build group filters if a group is selected
        if (selectedGroup) {
            const groupFilterItems: GridFilterItem[] = [
                {
                    id: 1,
                    columnField: 'groups__name',
                    operatorValue: 'contains',
                    value: selectedGroup,
                }
            ];
            cleanedFilters.items = [...cleanedFilters.items, ...groupFilterItems];
        }

        // Build the API request
        const options: IMuiDataRequest = {
            page: gridState.page,
            page_size: gridState.page_size,
            filters: cleanedFilters,
            sorts: gridState.sorts,
        };
        
        const auth_token: string = await getAccessToken();

        userApi.get(options, auth_token)
        .then((resp) => {
            if(resp) {
                if (resp.success) {
                    setGridData({
                        rows: resp.data,
                        row_count: resp.count!
                    });
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while attempting to get User data... \n" +
                        "please retry your last action. If you continue to experience issues, " +
                        "please contact customer support.",
                        "Unable to Load User data...",
                        "warning"
                    );
                }
            }
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [gridState, selectedGroup, getAccessToken, msgBox_Show]);

    // Store getGridData in ref so it can be called from deleteSelectedItems
    useEffect(() => {
        getGridDataRef.current = getGridData;
    }, [getGridData]);

    // Load group options when component mounts
    useEffect(() => {
        getGroupOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Track previous values to prevent unnecessary API calls
    const prevValuesRef = useRef<string>('');

    useEffect(() => {
        // Create a stable key from the values that matter
        const currentKey = JSON.stringify({
            page: gridState.page,
            page_size: gridState.page_size,
            filters: gridState.filters,
            sorts: gridState.sorts,
            selectedGroup: selectedGroup
        });

        // Only call API if values actually changed
        if (prevValuesRef.current !== currentKey) {
            prevValuesRef.current = currentKey;
            getGridData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridState.page, gridState.page_size, gridState.filters, gridState.sorts, selectedGroup]);

    const lvGridToolbar = () => {
        const isAdmin = userHasPrivilege([AuthRolesEnum.Licensee]);
        const canAddUser = userHasPrivilege([AuthRolesEnum.Licensee, AuthRolesEnum.ListOwner]);
        return (
            <ListViewToolbar
                onClickNew={onClickNew}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickReset={onClickReset}
                execSearch={execSearch}
                canView={isAdmin}
                canAdd={canAddUser}
                canUpdate={isAdmin}
                canDelete={isAdmin}
            />
        );
    }

    return (
        <>
            <Box
                sx={{
                    paddingTop: 3,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <FormTitle
                        title={"Users"}
                        titleIcon={"person_rounded_icon"}
                        typeVariant={"h4"}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            User Type:
                        </Typography>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: 120, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                            <Select
                                id="group-filter"
                                value={selectedGroup}
                                onChange={handleGroupChange}
                                displayEmpty
                                sx={{
                                    backgroundColor: 'background.paper',
                                    width: { xs: '100%', sm: 'auto' },
                                    '& .MuiSelect-select': {
                                        paddingY: 1,
                                    }
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {groupOptions.map((group) => {
                                    const displayName = UserGroupDisplay[group.group_name as UserGroup] || group.group_name;
                                    return (
                                        <MenuItem key={group.group_name} value={group.group_name}>
                                            {displayName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                    {isAuthenticated ? (
                        <Box sx={{ marginTop: 2 }}>
                            <DaGridContainer
                                sx={{
                                    height: 600,
                                    width: '100%',
                                }}
                            >
                                <DataGrid
                                    components={{
                                        LoadingOverlay: DaGridLoadingOverlay,
                                        Toolbar: lvGridToolbar,
                                    }}
                                    loading={isLoading}
                                    columns={columns}
                                    rows={gridData.rows}
                                    rowCount={gridData.row_count}
                                    getRowId={(row) => row.id}
                                    filterMode="server"
                                    paginationMode="server"
                                    sortingMode="server"
                                    pagination
                                    page={gridState.page}
                                    pageSize={gridState.page_size}
                                    onFilterModelChange={onGridFilterChange}
                                    onSortModelChange={onGridSortChange}
                                    onPageChange={onGridPageChange}
                                    onPageSizeChange={onGridPageSizeChange}
                                    rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                    headerHeight={DaGridSettings.headerRowHeight}
                                    rowHeight={DaGridSettings.rowHeight}
                                    checkboxSelection
                                    disableSelectionOnClick
                                    selectionModel={selectionModel}
                                    onSelectionModelChange={(newSelection) => {
                                        setSelectionModel(newSelection);
                                    }}
                                />
                            </DaGridContainer>
                        </Box>
                    ) : (
                        <Typography variant="h5">Please Login to use this system...</Typography>
                    )}
            </Box>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgBoxOpen}
                onClose={dlgBox_Close}
                {...dlgBoxProps}
            />
        </>
    );
}
